import React from 'react';
import { FieldTypes, Option } from '../../shared/KYCPolicy';
import { createEnDaContent, lang, translateContent } from '../../globals/Translations';
import { HTMLInputTypeAttribute } from 'react';
import DragAndDrop from './DragAndDrop'
import { CountryList } from '../../shared/Countries'
import colors from '../../globals/colors';
import { TranslatedTerms } from '../../globals/TranslatedTerms';
import styled from 'styled-components';
import { ReactComponent as GreenCheckIcon } from '../../assets/icon/green-check-icon.svg';
import { Checkbox, MenuItem, Select } from '@mui/material';

import PhoneInput from 'react-phone-number-input'
import { E164Number } from 'libphonenumber-js/min';
import 'react-phone-number-input/style.css'
import IBANLib from '../../globals/IBANLib';

type AvailableInputs = string | number | E164Number

type DynamicPropertyInputProps = {
    type: FieldTypes,
    fieldName: string,
    subTitle?: string,
    value?: AvailableInputs,
    setValue(props?: AvailableInputs): void,
    loading?: boolean,
    options?: Option[],
    id?: string,
    onEnter?: () => void
}

export const DynamicPropertyInput: React.FC<DynamicPropertyInputProps> = ({ type, fieldName, value, loading, setValue, options, subTitle, id, onEnter }) => {

    if (type === FieldTypes.PasswordField) return (
        <RegularTextInput fieldName={fieldName} subTitle={subTitle} id={id} onEnter={onEnter}
            setValue={setValue} loading={!!loading} value={value} options={{ password: true }} />
    )
    if (type === FieldTypes.TextField) return (
        <RegularTextInput fieldName={fieldName} subTitle={subTitle} id={id} onEnter={onEnter}
            setValue={setValue} loading={!!loading} value={value} />
    )
    if (type === FieldTypes.EmailField) return (
        <RegularTextInput fieldName={fieldName} subTitle={subTitle} id={id} onEnter={onEnter}
            setValue={setValue} loading={!!loading} value={value} type="email" />
    )
    if (type === FieldTypes.PhoneField) return (
        <InputTemplate loading={!!loading} fieldName={fieldName} subTitle={subTitle}>
            {/**@ts-ignore*/}
            <PhoneInput placeholder={fieldName} value={value} onChange={setValue} />
        </InputTemplate>
    )
    if (type === FieldTypes.FileField) return (
        <FileInput fieldName={fieldName} subTitle={subTitle} id={id} onEnter={onEnter}
            setValue={setValue} loading={!!loading} value={value} />
    )
    if (type === FieldTypes.AddressField) return (
        <RegularTextInput fieldName={fieldName} subTitle={subTitle} id={id} onEnter={onEnter}
            setValue={setValue} loading={!!loading} value={value} />
    )
    if (type === FieldTypes.CountrySelect) return (
        <InputTemplate loading={!!loading} fieldName={fieldName} subTitle={subTitle}>
            <select onChange={(e) => e.target.value ? setValue(e.target.value) : setValue(undefined)}>
                <option value={undefined}>{lang(TranslatedTerms.SELECT_ONE)}</option>
                {CountryList.map((item, key) => (
                    <option key={key} value={item.countryId}>{item.countryDisplayName}</option>
                ))}
            </select>
        </InputTemplate>
    )
    if (type === FieldTypes.Select) return (
        <SelectInput fieldName={fieldName} onSelect={(value: string) => setValue(value)} subTitle={subTitle} value={value?.toString()}
            options={options || []} itemRender={(item: Option) => lang(item.name)} getValue={(item: Option) => item.value}
        />
    )
    if (type === FieldTypes.BankAccount) return (
        <BankAccountInput fieldName={fieldName} subTitle={subTitle}
            setValue={setValue} loading={!!loading} value={value?.toString()} />
    )
    return <RegularTextInput fieldName={fieldName} subTitle={subTitle} id={id} onEnter={onEnter}
        setValue={setValue} loading={!!loading} value={value} />

}

type FieldOptions = {
    password?: boolean
}
type TextinputProps = {
    fieldName: string,
    subTitle?: string;
    value?: AvailableInputs,
    setValue(input: AvailableInputs): void,
    loading: boolean,
    type?: HTMLInputTypeAttribute,
    options?: FieldOptions,
    id?: string,
    onEnter?: () => void
}


export const FileInput: React.FC<TextinputProps> = ({ value, setValue, loading, fieldName, subTitle }) => {
    const height = 100

    return (
        <InputTemplate loading={loading} fieldName={fieldName} height={height} subTitle={subTitle}>
            <DragAndDrop setValue={setValue} height={height} value={value} fieldName={fieldName} />
        </InputTemplate>
    )
}

export const RegularTextInput: React.FC<TextinputProps> = ({ value, setValue, loading, fieldName, subTitle, options, id, onEnter, type }) => {

    const password = options && options.password === true

    const localType: HTMLInputTypeAttribute = password ? 'password' : type ? type : 'text'

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && onEnter) onEnter()
    }

    return (
        <InputTemplate loading={loading} fieldName={fieldName} subTitle={subTitle}>
            <input defaultValue={value} onChange={(e) => setValue(e.target.value)} type={localType} id={id} onKeyDown={handleKeyDown} />
        </InputTemplate>
    )
}

type RegularMultilineInputProps = {
    fieldName: string,
    subTitle?: string;
    value?: string,
    setValue(input: string): void,
    loading: boolean,
    id?: string,
    rows?: number,
}
export const RegularMultilineInput: React.FC<RegularMultilineInputProps> = ({ value, setValue, loading, fieldName, subTitle, id, rows }) => {

    return (
        <InputTemplate loading={loading} fieldName={fieldName} subTitle={subTitle}>
            <textarea defaultValue={value} onChange={(e) => setValue(e.target.value)} id={id} rows={rows} />
        </InputTemplate>
    )
}

type SelectProps = {
    options: Object[],
    itemRender(item: Object): string,
    onSelect(item: string): void,
    getValue(item: Object): string,
    fieldName: string,
    subTitle?: string,
    value?: string,
    hidePlaceholder?: boolean
}

export const defaultSelect = "no-selected"
export const SelectInput: React.FC<SelectProps> = ({ options, itemRender, getValue, onSelect, fieldName, value, subTitle, hidePlaceholder }) => {
    const [localValue, setLocalValue] = React.useState<string>(value || defaultSelect)
    React.useEffect(() => {
        if (value) setLocalValue(value)
        else setLocalValue(defaultSelect)
    }, [value])

    const handleChange = (value: string) => {
        onSelect(value)
        setLocalValue(value)
    }

    return (
        <InputTemplate loading={false} fieldName={fieldName} subTitle={subTitle}>
            <Select onChange={(e) => handleChange(e.target.value)} value={localValue} size="small">
                {!hidePlaceholder && <MenuItem value={defaultSelect}>{lang(TranslatedTerms.SELECT_ONE)}</MenuItem>}
                {options.map((item, key) => (<MenuItem key={key} value={getValue(item)}>{itemRender(item)}</MenuItem>))}
            </Select>
        </InputTemplate>
    )
}

type CheckboxProps = {
    description: string | JSX.Element,
    handleClick(): void,
    checked?: boolean
}
export const DefaultCheckbox: React.FC<CheckboxProps> = ({ description, handleClick, checked }) => {

    return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Checkbox onClick={handleClick} style={{ marginRight: 10 }} checked={checked} />
            <p className="small" style={{ marginTop: 5 }}>{description}</p>
        </div>
    )
}

type DangerButtonProps = {
    title: string,
    description: string,
    warningDescription: string,
    handleClick(): void
}

const InfoText = styled.span`
    margin: 10px;
    color: ${colors.grayText};
    font-size: 12px;
    text-align: right;
    background-color: ${colors.lighter};
    padding: 4px;
    border-radius: 10px;
`

export const DangerButton: React.FC<DangerButtonProps> = ({ title, description, warningDescription, handleClick }) => {

    const [showAreYouSure, setShowAreYouSure] = React.useState(false)

    const onClick = () => {
        if (showAreYouSure) { setShowAreYouSure(false); handleClick() }
        else setShowAreYouSure(true)
    }
    const handleCancel = () => setShowAreYouSure(false)

    return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {showAreYouSure && <InfoText>{lang(warningDescription)}</InfoText>}
            {!showAreYouSure && <InfoText>{lang(description)}</InfoText>}
            <button onClick={onClick} className="danger">{showAreYouSure ? lang(TranslatedTerms.ARE_YOU_SURE) + '?' : lang(title)}</button>
            {showAreYouSure && <button style={{ marginLeft: 10 }} className="light" onClick={handleCancel}>{lang(TranslatedTerms.CANCEL)}</button>}
        </div>
    )
}

export const ShareButton: React.FC<{ link: string }> = ({ link }) => {

    const [buttonClicked, setButtonClicked] = React.useState(false)

    const handleClick = () => {
        setButtonClicked(true)
        navigator.clipboard.writeText(link)
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {!buttonClicked && <button onClick={handleClick} className="light small">SHARE</button>}
            {buttonClicked && <span>Link copied!</span>}
            {buttonClicked && <GreenCheckIcon style={{ height: 20, marginLeft: 10 }} />}

        </div>
    )
}


type InputTemplateProps = { loading: boolean, fieldName: string, height?: number, subTitle?: string }

const FieldName = styled.span`
    font-weight: 400;
    color: ${colors.grayText};
`
const ErrorField = styled.span`
    font-weight: 400;
    color: ${colors.danger};
    font-size: 14px;
`

const LoadingTemplate: React.FC<{ fieldName?: string, height?: number }> = ({ fieldName, height }) => {

    return (
        <div >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldName className="space" style={{ fontSize: 14 }}>{lang(fieldName)}</FieldName>
                <div className="inputLoading" style={{ height }}></div>
            </div>
        </div>
    )
}


const InputTemplate: React.FC<InputTemplateProps> = ({ loading, fieldName, children, height, subTitle }) => {
    if (!!loading) return (
        <LoadingTemplate fieldName={fieldName} />
    )

    if (subTitle) return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldName style={{ fontSize: 14, marginTop: 5 }}>{lang(fieldName)}</FieldName>
                <FieldName style={{ fontSize: 10, lineHeight: "15px" }}>{lang(subTitle)}</FieldName>
                {children}
            </div>
        </div>
    )
    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldName className="space" style={{ fontSize: 14 }}>{lang(fieldName)}</FieldName>
                {children}
            </div>
        </div>
    )
}

export const ToolTip: React.FC<{ content: string }> = ({ content }) => {
    const [openToolTip, setOpenTooltip] = React.useState(false)

    return (
        <>
            <div style={{
                backgroundColor: colors.dark, paddingLeft: 10, paddingRight: 10, borderRadius: 10,
                position: 'absolute', width: 200, marginTop: -60,
                opacity: openToolTip ? 1 : 0, transition: '0.6s', zIndex: 100
            }}>
                <p className='small' style={{ color: colors.lightest, margin: 4, fontWeight: 'regular' }}>{content}</p>
            </div>
            <div
                style={{
                    backgroundColor: openToolTip ? colors.grayText : colors.dark, width: 20, height: 20, borderRadius: 10,
                    marginLeft: 5, marginRight: 5, marginTop: 4,
                    justifyContent: 'center', alignItems: 'center', display: 'flex', cursor: 'pointer'
                }}
                onMouseEnter={() => setOpenTooltip(true)} onMouseLeave={() => setOpenTooltip(false)} onClick={() => setOpenTooltip(!openToolTip)} >
                <span style={{ color: colors.lightest }}>?</span>
            </div>
        </>
    )
}


const BankAccountInput: React.FC<{
    value?: string,
    setValue(value: string): void,
    loading: boolean,
    fieldName: string,
    subTitle?: string,
}> = ({ value, setValue, loading, fieldName, subTitle }) => {
    const [localValue, setLocalValue] = React.useState(value || '')
    const [useIBAN, setUseIBAN] = React.useState(false)

    const [reg, account] = (localValue || '').split(' ')

    const showInvalidIBANMessage = useIBAN && !!localValue && !IBANLib.isValidIBANNumber(localValue)
    const showInvalidReg = !useIBAN && !!reg && reg.length !== 4

    const showInvalidAccount = !useIBAN && account !== undefined && account.length >= 1 && account.length !== 8

    React.useEffect(() => {
        if (value && !value.includes(' ') && IBANLib.isValidIBANNumber(value)) {
            setUseIBAN(true)
        }
        if (value) setLocalValue(value)

    }, [value])

    const handleSetValue = (val: string) => {
        setValue(val)
        setLocalValue(val)
    }


    const handleAccountValueChange = (props: { reg?: string, account?: string }) => {
        let newString = ""
        if (!props.reg) newString = reg + " "
        else newString = props.reg + " "

        if (!props.account) newString = newString + (account || '')
        else newString = newString = newString + props.account
        handleSetValue(newString)
    }

    if (loading) return <LoadingTemplate fieldName={fieldName} />

    const sharedStyles = (active: boolean): React.CSSProperties => ({
        fontSize: 14, cursor: 'pointer', padding: '0px 0px', flex: 1,
        borderBottomWidth: active ? 6: 4, borderBottomStyle: 'solid', textAlign: 'center',
        margin: '0px 6px', color: active ? colors.primary : colors.dark,
        borderBottomColor: active ? colors.primary : colors.dark,
        opacity: active ? 1 : 0.6
    })

    return (
        <div>
            <div className='space' style={{ display: 'flex', flexDirection: 'row' }}>
                <FieldName onClick={() => setUseIBAN(true)} style={{ ...sharedStyles(useIBAN) }}>IBAN</FieldName>
                <FieldName onClick={() => setUseIBAN(false)} style={{ ...sharedStyles(!useIBAN) }}>{translateContent(createEnDaContent("DK Bank account", "Brug dansk konto"))}</FieldName>
            </div>
            {useIBAN ? <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldName style={{ fontSize: 14 }}>IBAN</FieldName>
                <input defaultValue={localValue} onChange={(e) => handleSetValue(e.target.value)} />
                {showInvalidIBANMessage && <ErrorField>{translateContent(createEnDaContent("Invalid IBAN (Fx: DK5000400440116243)", "Ugyldigt IBAN (Fx: DK5000400440116243)"))}</ErrorField>}
            </div> :
                <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ display: 'flex', width: 120, flexDirection: 'column' }}>
                        <FieldName style={{ fontSize: 14 }}>{translateContent(createEnDaContent("Reg. number", "Reg nr"))}</FieldName>
                        <input defaultValue={reg} onChange={(e) => handleAccountValueChange({ reg: e.target.value })} type="number" />
                        {showInvalidReg && <ErrorField>{translateContent(createEnDaContent("Invalid (Fx: 0999)", "Ugyldigt (Fx: 0999)"))}</ErrorField>}
                    </div>
                    <div style={{ display: 'flex', flex: 5, flexDirection: 'column' }}>
                        <FieldName style={{ fontSize: 14 }}>{translateContent(createEnDaContent("Account number", "Konto nummer"))}</FieldName>
                        <input defaultValue={account} onChange={(e) => handleAccountValueChange({ account: e.target.value })} type="number" />
                        {showInvalidAccount && <ErrorField>{translateContent(createEnDaContent("Invalid (Fx: 0012345678)", "Ugyldigt (Fx: 0012345678)"))}</ErrorField>}
                    </div>
                </div>}
        </div>
    )
}

export default InputTemplate